import { createFileRoute, useRouter, Navigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/shared/ui';
import { Pagination } from '@/shared/ui/pagination/pagination';
import { createdBoxQueries } from '@/entities/sets/created/index';
import { CreatedBoxCardType, CreatedBoxListType } from '@/widgets/card';
import useVisible from '@/shared/hooks/useVisible';
import { CreatedBoxSearchFilter } from '@/widgets/filter';
import { SearchBarType1 } from '@/widgets/search-bar';
import { PublishedHistoryPopup } from '@/widgets/modal/ui/published-history-popup';
import { NoData } from '@/shared/ui/no-data';
import { CreatedDeleteButton } from '@/features/delete';
import { PublishButton } from '@/features/publish';
import {
    useCommonStore,
    useCreatedSearchStore,
    usePersistStore,
    useSearchStore,
} from '@/shared/lib/store';
import { CopyrightButton } from '@/features/guide';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import useToggle from '@/shared/hooks/useVisible';
import { sortGbArr, sortGbMap } from '@/shared/utils/created';
import { trackEvent } from 'vs-gs';

export const Route = createFileRoute('/sets/created-box/')({
    component: () => <Navigate to="/my-bank" replace />,
});

export function CreatedBoxComponent() {
    const router = useRouter();
    const { sUserId, sDefaultUserId } = usePersistStore();
    const { setInKeyword, searchPage, setSearchPage, setMyBankTab } = useSearchStore();
    const {
        keyword,
        setKeyword,
        listType,
        setListType,
        selected,
        setSelected,
        filterSelected,
        setFilterSelected,
        sortGb,
        setSortGb,
        setOpenSelected,
        setFirstFilterData,
        setFilteredData,
    } = useCreatedSearchStore();
    const { showModal, closeModal } = useModalStore();
    const { prevPath } = useCommonStore();
    const [checkList, setCheckList] = useState<DATA.Exam[]>([]);
    const [openBottomBar, setOpenBottomBar] = useState(false);

    const [totalPage, setTotalPage] = useState(0);

    const [selectOpen, setSelectOpen] = useToggle();

    const [selectData, setSelectData] = useState<DATA.Exam>();
    const [isVisible, setIsVisible] = useVisible();
    const [searchNoResult, setSearchNoResult] = useState<boolean>(false);

    useEffect(() => {
        if (
            !!prevPath &&
            !(prevPath.includes('sets/created-box/detail') || prevPath.includes('/edit'))
        ) {
            setListType('card');
            setKeyword('');
            setInKeyword('');
            setSearchPage(0);
            setOpenSelected([[], [], [], []]);
            setFilterSelected([[], [], []]);
            setSelected([[], [], [], []]);
            setSortGb('1');
            setFilteredData([]);
        }
    }, []);

    const filterList = [...filterSelected[0], ...filterSelected[1], ...filterSelected[2]];
    const queryParams = useMemo(
        () => ({
            keyword: keyword,
            textbkIds: selected[0].join(','),
            wrterId: sUserId,
            curriIdList:
                filterList && filterList.length > 0
                    ? filterList.map((v) => v.toString()).join(',')
                    : '',
            sortGbCd: sortGb,
            page: searchPage,
            size: listType === 'card' ? 16 : 5,
        }),
        [sUserId, sortGb, searchPage, keyword, listType, filterSelected]
    );

    const { data, refetch } = createdBoxQueries.getCreatedBoxList(queryParams, !!sUserId);

    const { data: createdHistoryList, isFetched } = createdBoxQueries.getCreatedHistoryList(
        {
            wrterId: sUserId,
        },
        !!sUserId
    );

    useEffect(() => {
        if (data) setTotalPage(data?.page?.totalPages);
    }, [data]);

    useEffect(() => {
        const filterList = checkList?.filter((obj) => selected[0].includes(obj.textbkId));
        setCheckList(filterList);
    }, [selected[0]]);

    const onRefetch = () => {
        setSearchNoResult(false);
        refetch();
    };

    const onSelect = (data: DATA.Exam) => {
        setSelectData(data);
        setIsVisible();
    };

    const handleCheck = (item: DATA.Exam) => {
        const isIncluded = checkList.find((obj) => obj.examId === item.examId);
        if (checkList.length > 0 && item.curriBookCd !== checkList[0].curriBookCd && !isIncluded) {
            sameCurriAlertPopup();
            return;
        }

        setCheckList((prev) => {
            const found = prev.find((v) => v.examId === item.examId);
            if (found) {
                // 이미 있다면 제거
                return prev.filter((v) => v.examId !== item.examId);
            } else {
                // 없으면 추가
                return [...prev, item];
            }
        });
    };

    const isAllSameCurri = !checkList.every((obj) => obj.curriBookCd === checkList[0].curriBookCd);

    const isChecked = (item: DATA.Exam) => {
        return checkList.some((v) => v.examId === item.examId);
    };

    const sameCurriAlertPopup = () => {
        showModal({
            isCustom: true,
            id: 'same-curri-error',
            initContents: () => (
                <Modal
                    overlay={false}
                    open={true}
                    onClose={() => closeModal('same-curri-error')}
                    title="안내"
                    description={
                        <>
                            같은 교과서의 평가 자료만
                            <br /> 복수 선택하여 발행할 수 있습니다.
                        </>
                    }
                ></Modal>
            ),
        });
    };

    const handleGoCreate = () => {
        const deploy = import.meta.env.VITE_DEPLOY;
        if (deploy === 'prod') {
            trackEvent({
                event: '스마트 문제은행',
                parameter: '출제하기',
                parameter_value: '내 출제함',
            });
        }
        router.navigate({ to: '/create' });
    };

    return (
        <>
            <Modal open={isVisible} onClose={setIsVisible} isCustom={true}>
                <PublishedHistoryPopup examId={selectData?.examId!} />
            </Modal>
            <section className="contwrap submittedBox">
                <a
                    href={import.meta.env.VITE_ENGLISH_MASTER_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="floatBanner ai_english"
                >
                    <span className="blind">AI 영어 지문 마스터로 나만의 문항 만들기</span>
                </a>
                <a
                    href={import.meta.env.VITE_VIVACLASS_DOMAIN}
                    target="_blank"
                    rel="noreferrer"
                    className="floatBanner vivaclass"
                >
                    <span className="blind">비바클래스 수업에서 온라인 평가 활용하기</span>
                </a>
                <div className="contInner" id="card_type">
                    <div className="tabMenu2 subject">
                        <ul>
                            <li className="on">
                                <a>내 출제함</a>
                            </li>
                            <li onClick={() => setMyBankTab('published')}>
                                <a>내 발행함</a>
                            </li>
                        </ul>
                    </div>
                    {createdHistoryList && createdHistoryList.textbkList.length > 0 ? (
                        <div className="searchWrap">
                            <SearchBarType1
                                title="자료명 검색"
                                placeholder="검색하실 자료명을 입력해주세요."
                                inputTitle="내 출제함 자료명 검색"
                                setKeyword={setKeyword}
                                setSearchNoResult={setSearchNoResult}
                            />
                            <CreatedBoxSearchFilter
                                createdHistoryList={createdHistoryList}
                                selected={selected}
                                setSelected={setSelected}
                                setFilterSelected={setFilterSelected}
                            />
                            <div className="view_wrap">
                                <div className="viewBox">
                                    <div className="report-content ttukttak_report-content">
                                        <div className="layout-type1">
                                            <div className="class-datas-info block">
                                                <div className="datas-utill">
                                                    <p className="total-text">
                                                        <strong>
                                                            총{' '}
                                                            {data?.page?.totalElements?.toLocaleString() ??
                                                                0}
                                                            개
                                                        </strong>
                                                        의 자료가 있습니다.
                                                    </p>
                                                    <div className="view-sort">
                                                        <div className="option-view">
                                                            <button
                                                                className={`btn btn-icon collectView ${listType === 'card' && 'active'}`}
                                                                onClick={() => {
                                                                    setListType('card');
                                                                    setSearchPage(0);
                                                                }}
                                                            >
                                                                카드형
                                                            </button>
                                                            <button
                                                                className={`btn btn-icon listView ${listType === 'list' && 'active'}`}
                                                                onClick={() => {
                                                                    setListType('list');
                                                                    setSearchPage(0);
                                                                }}
                                                            >
                                                                리스트형
                                                            </button>
                                                        </div>
                                                        <div className="dropdown-wrap">
                                                            <div className="dropdown-box">
                                                                <div
                                                                    className={`dropdown-btn ${selectOpen && 'isOpen'}`}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={setSelectOpen}
                                                                    >
                                                                        <span className="title">
                                                                            {sortGbMap[sortGb]}
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                                <div
                                                                    className={`dropdown-content ${selectOpen && 'isOpen'}`}
                                                                >
                                                                    <div className="option-type-default">
                                                                        {sortGbArr.map(
                                                                            (item, idx) => (
                                                                                <div
                                                                                    key={`sort-gb-${idx}`}
                                                                                    className={`option ${sortGb === item.value && 'isCurrent'}`}
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setSortGb(
                                                                                                item.value
                                                                                            );
                                                                                            setSelectOpen();
                                                                                        }}
                                                                                    >
                                                                                        {item.label}
                                                                                    </button>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {searchNoResult && data?.examList.length === 0 ? (
                                                <NoData
                                                    titleBlue={keyword}
                                                    titleBlack="검색 결과 없습니다."
                                                    description="다른 검색어로 검색하거나 교과서, 단원을 다시 선택하여 검색해 보세요!"
                                                />
                                            ) : (
                                                <div className="class-datas-wrap block">
                                                    <div className="class-datas">
                                                        <ul
                                                            className={`data-list ${listType}-type`}
                                                        >
                                                            {data?.examList?.map((e, i) =>
                                                                listType === 'card' ? (
                                                                    <CreatedBoxCardType
                                                                        key={`card-type-${i}`}
                                                                        data={e}
                                                                        index={i}
                                                                        checked={isChecked(e)}
                                                                        handleCheck={handleCheck}
                                                                        onRefetch={onRefetch}
                                                                        onSelect={onSelect}
                                                                    />
                                                                ) : (
                                                                    <CreatedBoxListType
                                                                        key={`list-type-${i}`}
                                                                        data={e}
                                                                        index={i}
                                                                        checked={isChecked(e)}
                                                                        handleCheck={handleCheck}
                                                                        onSelect={onSelect}
                                                                        onRefetch={onRefetch}
                                                                    />
                                                                )
                                                            )}
                                                        </ul>
                                                        <div className="board-footer">
                                                            <Pagination
                                                                currentPage={searchPage}
                                                                totalPages={totalPage}
                                                                onChange={(page: number) =>
                                                                    setSearchPage(page)
                                                                }
                                                                pageBtnCount={10}
                                                            />
                                                            <div className="board-footer__btn-box">
                                                                <CopyrightButton />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isFetched && (
                                <NoData
                                    titleBlue={'아직 출제한 자료가 없습니다.'}
                                    description="출제하기 버튼 클릭해 자료를 출제해 보세요!"
                                    buttonText={'출제하기'}
                                    onButtonClick={handleGoCreate}
                                />
                            )}
                        </>
                    )}
                </div>
            </section>
            {/* 다중 선택 기능 바 */}
            {createdHistoryList && createdHistoryList.textbkList.length > 0 && (
                <div className="selectBar_wrap">
                    <div className={`accordionBox flex_c ${openBottomBar && 'on'}`}>
                        <div className="selectList flex_b">
                            <div className="title">선택한 자료</div>
                            <ul>
                                {checkList.map((e, i) => (
                                    <li key={`select-check-${i}`}>
                                        <div className="selectbar_box">
                                            <span className="depth_tit1">{e.name}</span>
                                            {/* <span className="depth_tit2">{e.name}</span> */}
                                            {/* <span className="depth_tit3">
                    [3차시] 세 자리 수의 덧셈을 알아볼까요(2)
                  </span>
                  <span className="depth_tit4">일이삼사오육칠팔구십</span> */}
                                        </div>
                                        <button
                                            type="button"
                                            className="cancle"
                                            onClick={() => handleCheck(e)}
                                        >
                                            선택 해제
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <button
                            type="button"
                            className={`btn btn-default icon accordionBox_close on ${!openBottomBar && ''}`}
                            onClick={() => setOpenBottomBar(!openBottomBar)}
                        >
                            <i></i>닫기
                        </button>
                    </div>
                    <div className="select_bar">
                        <div className="accordionBtn">
                            <button
                                type="button"
                                className={`btn active ${!openBottomBar && 'on'}`}
                                onClick={() => setOpenBottomBar(!openBottomBar)}
                            >
                                <i></i>
                                <span className="arrowpd">총 {checkList.length}개 선택</span>
                            </button>
                        </div>
                        <div className="btnwrap flex_c">
                            <CreatedDeleteButton
                                styles={{ button: 'btn btn-default small blue delet' }}
                                buttonText="삭제"
                                checkList={checkList.map((v) => v.examId.toString())}
                                onRefetch={refetch}
                            />
                            <PublishButton
                                checkList={checkList}
                                icon={true}
                                buttonText="발행하기"
                                styles={{ button: 'btn btn-default white icon publish_b' }}
                                disabled={isAllSameCurri}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
